import { type ReactNode, useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useUserAccount } from '../hooks/useUserAccount'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { IonSpinner } from '@ionic/react'
import { ServerErrorScreen } from './ServerErrorScreen'
import { SplashScreen } from '@capacitor/splash-screen'
import { Capacitor } from '@capacitor/core'

interface AppInitializerProps {
  children: ReactNode
}

// Check if error is a network/server error
const isServerError = (error: Error | undefined): boolean => {
  if (!error) return false

  // Check for Apollo network errors - Apollo wraps errors in an object with networkError
  if ('networkError' in error && error.networkError !== null) {
    return true
  }

  // Check for common network error messages
  const message = error.message?.toLowerCase() || ''
  return (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('failed to fetch') ||
    message.includes('network request failed') ||
    message.includes('networkerror') ||
    message.includes('connection refused') ||
    message.includes('err_connection_refused') ||
    message.includes('econnrefused')
  )
}

export const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const { userAccount, error: userAccountError, loading: userAccountLoading } = useUserAccount()
  const { error: featureFlagsError, loading: featureFlagsLoading } = useFeatureFlags()
  const [shouldShowError, setShouldShowError] = useState(false)
  const [loadingTimeout, setLoadingTimeout] = useState(false)

  // Log errors for debugging
  useEffect(() => {
    if (userAccountError) {
      console.error('[AppInitializer] UserAccount error:', userAccountError)
    }
    if (featureFlagsError) {
      console.error('[AppInitializer] FeatureFlags error:', featureFlagsError)
    }
  }, [userAccountError, featureFlagsError])

  // Determine if we have a critical server error
  const hasServerError =
    (!userAccount && isServerError(userAccountError)) || isServerError(featureFlagsError)

  // Detect if we're stuck loading for too long (possible server down)
  const isInitializing = authLoading ||
    (isAuthenticated && (userAccountLoading || featureFlagsLoading))

  useEffect(() => {
    if (isInitializing && !hasServerError) {
      // If loading takes more than 10 seconds, assume server is down
      const timer = setTimeout(() => {
        console.warn('[AppInitializer] Loading timeout - assuming server is down')
        setLoadingTimeout(true)
      }, 10000)
      return () => {
        clearTimeout(timer)
        setLoadingTimeout(false)
      }
    }
  }, [isInitializing, hasServerError])

  useEffect(() => {
    if (!isInitializing && Capacitor.isNativePlatform()) {
      SplashScreen.hide({ fadeOutDuration: 300 })
    }
  }, [isInitializing])

  useEffect(() => {
    // Show error if we have a server error or if loading times out
    if ((hasServerError || loadingTimeout) && !authLoading) {
      if (Capacitor.isNativePlatform()) SplashScreen.hide({ fadeOutDuration: 300 })
      const timer = setTimeout(() => {
        setShouldShowError(true)
      }, 500)
      return () => clearTimeout(timer)
    } else {
      const timer = setTimeout(() => setShouldShowError(false), 0)
      return () => clearTimeout(timer)
    }
  }, [hasServerError, loadingTimeout, authLoading])

  if (isInitializing && !shouldShowError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <IonSpinner name="crescent" className="w-12 h-12" />
      </div>
    )
  }

  // Show error screen if server is unreachable
  if (shouldShowError) {
    return <ServerErrorScreen onRetry={() => window.location.reload()} />
  }

  // All good, render the app
  return <>{children}</>
}
